import type { HabitaResponsible } from '../../shared/models';
import { HttpRepository } from './HttpRepository';
import { getHabitaResponsiblesUrl } from './habitaApiUrls';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeResponsible(value: unknown): HabitaResponsible | null {
  if (!isRecord(value)) return null;

  const email = cleanText(value.email);
  const name = cleanText(value.name);
  if (!email && !name) return null;

  return {
    email,
    name,
  };
}

function normalizeResponsiblesPayload(value: unknown): HabitaResponsible[] {
  const rawItems = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.responsibles)
      ? value.responsibles
      : [];
  const seen = new Set<string>();
  const responsibles: HabitaResponsible[] = [];

  rawItems.forEach((item) => {
    const responsible = normalizeResponsible(item);
    if (!responsible) return;

    const key = responsible.email || responsible.name;
    if (seen.has(key)) return;
    seen.add(key);
    responsibles.push(responsible);
  });

  return responsibles.sort((left, right) => (
    (left.name || left.email).localeCompare(right.name || right.email, 'pt-BR')
  ));
}

export class ResponsiblesRepository {
  private httpRepository = new HttpRepository();

  async fetchResponsibles(): Promise<HabitaResponsible[]> {
    const response = await this.httpRepository.get<unknown>(getHabitaResponsiblesUrl());
    return normalizeResponsiblesPayload(response.data);
  }
}
